import React, { useState, useRef, MouseEvent, useEffect, useCallback } from 'react';
import { FloorPlan, SeatStatus, Desk, Room, Facility } from '../types';
import { Minimap } from './Minimap';
import './FloorPlanViewer.css';
import { ElevatorIcon } from './icons/ElevatorIcon';
import { StairwayIcon } from './icons/StairwayIcon'; // Import new icon
import { KitchenIcon } from './icons/KitchenIcon';   // Import new icon
import { toScreenPlane, toWorld } from '../utils/transform'; // <-- IMPORT a new functions

interface FloorPlanViewerProps {
  planData: FloorPlan;
  statusData: SeatStatus[];
  onSeatClick: (deskId: string) => void;
  userReservationId?: string | null;
  focusedDeskId?: string | null; // NEW PROP
}

const getDeskPosition = (desk: Desk, room: Room) => {
    const positions: { [key: string]: { top: string, left: string } } = {
        'left': { top: '50%', left: '15%' },
        'right': { top: '50%', left: '55%' },
        'left window': { top: '5%', left: '15%' },
        'right window': { top: '5%', left: '55%' },
    };
    return positions[desk.location] || { top: '50%', left: '50%' };
};
// --- AABB (Axis-Aligned Bounding Box) intersection helper ---
interface BoundingBox { x: number; y: number; width: number; height: number; }
const doBoxesIntersect = (boxA: BoundingBox, boxB: BoundingBox) => {
    return (
      boxA.x < boxB.x + boxB.width &&
      boxA.x + boxA.width > boxB.x &&
      boxA.y < boxB.y + boxB.height &&
      boxA.y + boxA.height > boxB.y
    );
};
export const FloorPlanViewer = ({ planData, statusData, onSeatClick, userReservationId, focusedDeskId }: FloorPlanViewerProps) => {
  const [viewState, setViewState] = useState({
    scale: 0.5,
    translateX: -1700,
    translateY: 900,
  });
  
  const [isDragging, setIsDragging] = useState(false);
  const [startPos, setStartPos] = useState({ x: 0, y: 0 });
  
  const viewportRef = useRef<HTMLDivElement>(null);
  const [viewerDimensions, setViewerDimensions] = useState({ width: 0, height: 0 });
  const didFocus = useRef(false); // Ref to prevent re-focusing on every render
  // Use ResizeObserver to reliably get viewport dimensions for the minimap
  useEffect(() => {
    const viewportElement = viewportRef.current;
    if (!viewportElement) return;

    const resizeObserver = new ResizeObserver(entries => {
      if (entries[0]) {
        const { width, height } = entries[0].contentRect;
        setViewerDimensions({ width, height });
      }
    });

    resizeObserver.observe(viewportElement);

    return () => {
      resizeObserver.disconnect();
    };
  }, []);

  // Memoize the handleWheel function to prevent re-creation
  const handleWheel = useCallback((e: WheelEvent) => {
    e.preventDefault();
    const viewportElement = viewportRef.current;
    if (!viewportElement) return;

    setViewState(prev => {
        const rect = viewportElement.getBoundingClientRect();
        
        // 1. Get mouse position relative to the viewport
        const mouseX = e.clientX - rect.left;
        const mouseY = e.clientY - rect.top;
        
        // 2. Calculate the new scale
        const scaleAmount = -e.deltaY * 0.001;
        const newScale = Math.max(0.1, Math.min(2, prev.scale + scaleAmount));

        // 3. Calculate the new translation to keep the mouse position fixed
        // This formula finds where the mouse is on the canvas, and then adjusts
        // the translation so that point remains in the same screen position after scaling.
        const newTranslateX = mouseX - (mouseX - prev.translateX) * (newScale / prev.scale);
        const newTranslateY = mouseY - (mouseY - prev.translateY) * (newScale / prev.scale);

        return {
            scale: newScale,
            translateX: newTranslateX,
            translateY: newTranslateY
        };
    });
  }, []); // The dependency array is empty because we use the functional update form of setViewState

  useEffect(() => {
    const viewportElement = viewportRef.current;
    if (viewportElement) {
      viewportElement.addEventListener('wheel', handleWheel, { passive: false });
    }
    return () => {
      if (viewportElement) {
        viewportElement.removeEventListener('wheel', handleWheel);
      }
    };
  }, [handleWheel]);

 // --- REFACTORED FOCUSING LOGIC ---
 // REFACTORED FOCUSING LOGIC WITH DEBUG OUTPUT
 useEffect(() => {
  if (focusedDeskId && !didFocus.current && planData && viewerDimensions.width > 0) {
    const desk = planData.desks.find(d => d.id === focusedDeskId);
    const room = desk ? planData.rooms.find(r => r.id === desk.room) : undefined;
    
    if (room) {
      console.groupCollapsed(`[Focus] Calculating focus for Room ${room.id}`);
      const newScale = 0.8;

      // 1. Define the center of the target room in world coordinates
      const worldTarget = {
          x: room.position.x + room.size.width / 2,
          y: room.position.y + room.size.height / 2
      };
      console.log('Target world coordinates:', worldTarget);
      
      // 2. Transform the world target to the screen's 2D plane to know its rotated position
      const screenPlaneTarget = toScreenPlane(worldTarget.x, worldTarget.y);
      console.log('Target screen plane coordinates:', screenPlaneTarget);

      // 3. Calculate the required translation to move this screen plane point to the center of the viewport
      // We use the viewer dimensions to find the center of the screen
      const newTranslateX = (viewerDimensions.width / 2) - (screenPlaneTarget.x * 0.9);
      const newTranslateY = (viewerDimensions.height / 2) + (screenPlaneTarget.y * 0.9);
      console.log('Calculated TranslateX:', newTranslateX, 'TranslateY:', newTranslateY);
      console.groupEnd();
      
      setViewState({
        scale: newScale,
        translateX: newTranslateX,
        translateY: newTranslateY,
      });

      didFocus.current = true;
    }
  }
}, [focusedDeskId, planData, viewerDimensions]);

// --- NEW: DEBUG CLICK HANDLER ---
const handleDebugClick = (e: MouseEvent) => {
  // We only want this to fire on the background, not on desk clicks
  if (e.target !== e.currentTarget) {
      return;
  }

  console.groupCollapsed(`[Debug] Visible items at ${new Date().toLocaleTimeString()}`);
  
  // 1. Calculate the viewport's bounds in the canvas's coordinate system
  const { scale, translateX, translateY } = viewState;
  const viewportBounds: BoundingBox = {
      x: (0 - translateX) / scale,
      y: (0 - translateY) / scale,
      width: viewerDimensions.width / scale,
      height: viewerDimensions.height / scale,
  };
  console.log("Viewport Bounds (in canvas coordinates):", {
      x: Math.round(viewportBounds.x),
      y: Math.round(viewportBounds.y),
      width: Math.round(viewportBounds.width),
      height: Math.round(viewportBounds.height)
  });

  const visibleRooms: string[] = [];
  const visibleDesks: string[] = [];
  
  // 2. Check rooms for visibility
  planData.rooms.forEach(room => {
      const roomBounds: BoundingBox = {
          x: room.position.x - planData.dimensions.minX,
          y: room.position.y - planData.dimensions.minY,
          width: room.size.width - 20, // Account for visual margin
          height: room.size.height - 20,
      };
      if (doBoxesIntersect(viewportBounds, roomBounds)) {
          visibleRooms.push(`Room ${room.id}: (x: ${Math.round(roomBounds.x)}, y: ${Math.round(roomBounds.y)})`);
      }
  });

  // 3. Check desks for visibility
  // NOTE: These dimensions are hardcoded to match the CSS for simplicity in this debug tool.
  const DESK_WIDTH = 70;
  const DESK_HEIGHT = 160;

  planData.desks.forEach(desk => {
      const room = planData.rooms.find(r => r.id === desk.room);
      if (!room) return;

      const roomPosX = room.position.x - planData.dimensions.minX;
      const roomPosY = room.position.y - planData.dimensions.minY;
      const posPercent = getDeskPosition(desk, room);
      
      // Convert percentage-based position to absolute pixel position on the canvas
      const deskOffsetX = (parseFloat(posPercent.left) / 100) * (room.size.width - 20);
      const deskOffsetY = (parseFloat(posPercent.top) / 100) * (room.size.height - 20);

      const deskBounds: BoundingBox = {
          x: roomPosX + deskOffsetX - (DESK_WIDTH / 2), // Center the desk on the percentage point
          y: roomPosY + deskOffsetY - (DESK_HEIGHT / 2),
          width: DESK_WIDTH,
          height: DESK_HEIGHT
      };

      if (doBoxesIntersect(viewportBounds, deskBounds)) {
          visibleDesks.push(`Desk ${desk.name}: (x: ${Math.round(deskBounds.x)}, y: ${Math.round(deskBounds.y)})`);
      }
  });
  
  console.log("--- Visible Rooms ---");
  visibleRooms.forEach(r => console.log(r));
  console.log("--- Visible Desks ---");
  visibleDesks.forEach(d => console.log(d));

  console.groupEnd();
};

  const statusMap = new Map(statusData.map(s => [s.seatId, s.status]));

  const handleMouseDown = (e: MouseEvent) => {
    e.preventDefault();
    setIsDragging(true);
    setStartPos({ x: e.clientX - viewState.translateX, y: e.clientY - viewState.translateY });
    if(viewportRef.current) viewportRef.current.style.cursor = 'grabbing';
  };

  const handleMouseMove = (e: MouseEvent) => {
    if (!isDragging) return;
    e.preventDefault();
    const x = e.clientX - startPos.x;
    const y = e.clientY - startPos.y;
    setViewState(prev => ({ ...prev, translateX: x, translateY: y }));
  };

  const handleMouseUp = (e: MouseEvent) => {
    setIsDragging(false);
    if(viewportRef.current) viewportRef.current.style.cursor = 'grab';
  };

  const handleZoom = (direction: 'in' | 'out') => {
    const amount = direction === 'in' ? 0.1 : -0.1;
    setViewState(prev => ({
        ...prev,
        scale: Math.max(0.1, Math.min(2, prev.scale + amount)),
      }));
  } 
  const handleJump = ({ x, y }: { x: number; y: number }) => {
    const worldTarget = { x, y };

    // This logic is now identical to the focusing logic
    const screenPlaneTarget = toScreenPlane(worldTarget.x, worldTarget.y);
    
    setViewState(prev => {
        const newTranslateX = (viewerDimensions.width / 2) - (screenPlaneTarget.x * prev.scale);
        const newTranslateY = (viewerDimensions.height / 2) - (screenPlaneTarget.y * prev.scale);
        return {
            ...prev,
            translateX: newTranslateX,
            translateY: newTranslateY,
        }
    });
  };
  const renderFacilityContent = (facility: Facility) => {
    switch(facility.type) {
      case 'elevator':
        return <ElevatorIcon className="facility-icon" />;
      case 'stairs':
        return <StairwayIcon className="facility-icon" />;
      case 'kitchen':
        return <KitchenIcon className="facility-icon" />;
      default:
        return <div className="facility-label">{facility.name}</div>;
    }
  };

  return (
    <div className="viewer-container">
      <Minimap 
        planData={planData}
        viewState={viewState}
        viewerDimensions={viewerDimensions}
        onJump={handleJump}
      />
      <div className="zoom-controls">
          <button onClick={() => handleZoom('in')}>+</button>
          <button onClick={() => handleZoom('out')}>-</button>
      </div>
      <div
        ref={viewportRef}
        className="floorplan-viewport"
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
        onMouseLeave={handleMouseUp}
        onClick={handleDebugClick}
      >
        <div
          className="floorplan-canvas"
          style={{
            transform: `translateX(${viewState.translateX}px) translateY(${viewState.translateY}px) scale(${viewState.scale})`,
          }}
        >
          <div className="floorplan-3d-perspective">
            <div 
              className="floor-plate"
              style={{
                width: `${planData.dimensions.width}px`,
                height: `${planData.dimensions.height}px`,
                left: `${5}px`, 
                bottom: `${10}px`,
              }}
            ></div>
            {/* Render Bookable Rooms */}
            {planData.rooms.map(room => {
              const desksInRoom = planData.desks.filter(d => d.room === room.id);
              return (
                <div
                  key={room.id}
                  className="room"
                  style={{
                    left: `${room.position.x}px`,
                    bottom: `${room.position.y}px`,
                    width: `${room.size.width-20}px`,
                    height: `${room.size.height-20}px`,
                  }}
                >
                  <div className="room-label">{room.id}</div>
                  {desksInRoom.map(desk => {
                    const status = statusMap.get(desk.id) || 'available';
                    const isMyBooking = desk.id === userReservationId;
                    const classNames = `desk ${status} ${isMyBooking ? 'my-booking' : ''}`;
                    const pos = getDeskPosition(desk, room);
                    return (
                        <div
                            key={desk.id}
                            className={classNames}
                            style={{ top: pos.top, left: pos.left }}
                            onClick={(e) => {
                                e.stopPropagation();
                                if (status === 'available' || isMyBooking) {
                                  onSeatClick(desk.id);
                                }
                            }}
                        >
                            <div className="desk-name">{desk.name}</div>
                        </div>
                    )
                  })}
                </div>
              );
            })}
            {/* Render Non-Bookable Facilities */}
            {planData.facilities.map(facility => (
              <div
                key={facility.id}
                className={`facility facility-${facility.type}`}
                style={{
                  left: `${facility.position.x}px`,
                  bottom: `${facility.position.y}px`,
                  width: `${facility.size.width - 20}px`,
                  height: `${facility.size.height - 20}px`,
                }}
              >
                {/* USE THE NEW RENDER HELPER FUNCTION */}
                {renderFacilityContent(facility)}
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};